import { NgZone } from '@angular/core'
import { Subject } from 'rxjs/Subject'
import { Observable } from 'rxjs/Observable'
import { DomUtils } from '../..'
import { Uri } from '../../core/models'

import { IMathJaxConfigOptions } from './math-jax-config-options.interface'
import { MATH_JAX_CONFIG } from './math-jax-config'

interface MathJaxHub {
  Queue: (...args: any[]) => void
  Config: (IMathJaxConfigOptions) => void
  Configured: () => void
}

interface MathJaxApi {
  Hub: MathJaxHub
}

export class MathJaxService {

  private _onMathJaxLoaded: Observable<MathJaxApi>
  private _currentConfig: IMathJaxConfigOptions
  private _isMathJaxConfigured: boolean = false
  private _isMathJaxLoaded: boolean = false

  private static mathJaxLoadedWait = {
    periodsWaitedCount: 0,
    totalTimeWaitedMs: 0
  }

  private get _mathJaxApi(): MathJaxApi {
    return this._window['MathJax'] as MathJaxApi
  }

  constructor(private _window: Window, private _domUtils: DomUtils, private _ngZone: NgZone) {
    this._onMathJaxLoaded = new Subject<MathJaxApi>()
    this._onMathJaxLoaded.subscribe((MathJax) => this.configure())
    this._runWhenMathJaxLoaded((MathJax) => {
      this._isMathJaxLoaded = true
      const initialEmitter = this._onMathJaxLoaded as Subject<MathJaxApi>
      this._onMathJaxLoaded = Observable.of(MathJax)
      initialEmitter.next(MathJax)
      initialEmitter.complete()
    })
  }

  public loadMathJax() {
    if (this._isMathJaxLoaded) { return }

    this._addConfiguredMathJaxScriptTag(MATH_JAX_CONFIG.URL)
  }

  public configure(options: IMathJaxConfigOptions = {}) {
    const withCurrentOptions = Object.assign({}, this._currentConfig, options)
    this._currentConfig = this._getDefaultedMathJaxConfigOptions(withCurrentOptions)

    this._onMathJaxLoaded.subscribe((MathJax) => {
      MathJax.Hub.Config(this._currentConfig)
      if (!this._isMathJaxConfigured) {
        MathJax.Hub.Configured()
        this._isMathJaxConfigured = true
      }
    })
  }

  public renderMath(elementOrSelectorToRender?: Element|string) {
    this._onMathJaxLoaded.subscribe((MathJax) => {
      const mathJaxTypesetCallbackArgs = this._determineRenderArgs(elementOrSelectorToRender)
      // Processing entire page or selected element with MathJax outside of Angular,
      // so that it does not interfere with Angular's change detection
      this._ngZone.runOutsideAngular(() => MathJax.Hub.Queue(mathJaxTypesetCallbackArgs))
    })
  }

  private _determineRenderArgs(elementOrSelectorToRender?: Element|string) {
    const mathJaxTypesetCallbackArgs = ['Typeset', this._mathJaxApi.Hub] as any[]
    if (elementOrSelectorToRender) {
      mathJaxTypesetCallbackArgs.push(this._domUtils.getElement(elementOrSelectorToRender))
    }
    return mathJaxTypesetCallbackArgs
  }

  private _runWhenMathJaxLoaded(callback: (api: MathJaxApi) => void) {
    if (this._mathJaxApi) {
      return callback.call(this, this._mathJaxApi)
    }
    const waitInfo = this._computeNextMathJaxLoadWaitInfo()
    if (waitInfo.isTimedOut) {
      throw new Error(`Wait period for MathJax to load timed out; ensure connectivity`)
    }
    this._ngZone.runOutsideAngular(() => {
      setTimeout(() => this._runWhenMathJaxLoaded(callback), waitInfo.currentPeriodDurationMs)
    })
  }

  private _computeNextMathJaxLoadWaitInfo() {
    // computing the time to wait in case MathJax has not been loaded yet
    // looping faster initially, slowing down over time
    // by adding the initial wait period after every 10th iteration
    const periodsWaitedCount = ++MathJaxService.mathJaxLoadedWait.periodsWaitedCount
    const tenthPeriodWaitedCount = Math.ceil(periodsWaitedCount / 10)
    const currentPeriodDurationMs = tenthPeriodWaitedCount * MATH_JAX_CONFIG.LOADED_WAIT.INITIAL_PERIOD_DURATION_MS
    MathJaxService.mathJaxLoadedWait.totalTimeWaitedMs += currentPeriodDurationMs
    return {
      isTimedOut: MathJaxService.mathJaxLoadedWait.totalTimeWaitedMs >=
                  MATH_JAX_CONFIG.LOADED_WAIT.TIMEOUT_AFTER_MS,
      currentPeriodDurationMs: currentPeriodDurationMs
    }
  }

  private _addConfiguredMathJaxScriptTag(url) {
    const uri = new Uri(url)
    Object.keys(MATH_JAX_CONFIG.LOAD_PARAMS).forEach((key) => {
      uri.addQueryStringPart(key, MATH_JAX_CONFIG.LOAD_PARAMS[key])
    })

    const script = this._domUtils.createElement('script') as HTMLScriptElement
    script.type = 'text/javascript'; script.src = uri.toString(); script.async = true
    this._domUtils.getElement('head').appendChild(script)
  }

  private _getDefaultedMathJaxConfigOptions(options: IMathJaxConfigOptions) {
    return Object.assign({}, MATH_JAX_CONFIG.PROCESSOR_OPTIONS, options)
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/services/math-jax/math-jax.service.ts